// Section Metadata
// This file contains descriptive metadata for each section type
//
// MANDATORY: every id in SECTION_TYPES needs an entry here. There is no
// fallback worth shipping — consumers that cannot find one fall back to the raw
// id, so a missing entry shows editors "Section 3006" instead of a name.
//
// Read by, among others, **Widget Management** (`/admin/widget-management`),
// which groups the cross-page widget inventory by section and uses `name` as the
// group heading and `icon` as its marker. That screen is where someone decides
// whether a widget is safe to delete from live pages, so an unnamed section is a
// real hazard, not a cosmetic gap.
//
// Adding a section? See the full checklist at the top of ./sectionTypes.ts.

import { SECTION_TYPES } from "./sectionTypes";

// Metadata for each section type
export const SECTION_METADATA = {
  [SECTION_TYPES.BANNER]: {
    name: "Banner",
    description: "Announcement banners",
    icon: "🏷️",
  },
  [SECTION_TYPES.HEADER]: {
    name: "Header",
    description: "Navigation headers",
    icon: "📌",
  },
  [SECTION_TYPES.MENU_ITEM]: {
    name: "Menu Item",
    description: "Hierarchical navigation menu",
    icon: "📋",
  },
  [SECTION_TYPES.ABOUT]: {
    name: "About",
    description: "About blocks (text, media, institute layout, etc.)",
    icon: "ℹ️",
  },
  [SECTION_TYPES.KEY_NUMBERS]: {
    name: "Key Numbers",
    description: "Key statistics",
    icon: "🔢",
  },
  [SECTION_TYPES.DIGITAL_LEARNING]: {
    name: "Digital Learning",
    description: "Digital learning section with pre-title, title, CTA and metadata",
    icon: "📱",
  },
  [SECTION_TYPES.VISIONARIES]: {
    name: "Visionaries",
    description: "Led by visionaries carousel with tabbed categories and profiles",
    icon: "🌟",
  },
  [SECTION_TYPES.LEARN_FROM_LEADERS]: {
    name: "Learn from Leaders",
    description: "Headline and horizontal leader cards (split layout)",
    icon: "✨",
  },
  [SECTION_TYPES.ACCREDITATION]: {
    name: "Accreditation",
    description: "Accreditations",
    icon: "✅",
  },
  [SECTION_TYPES.ACADEMIC_DELIVERABLES]: {
    name: "Academic Deliverables",
    description: "Academic programs",
    icon: "📚",
  },
  [SECTION_TYPES.SCHOLAR]: {
    name: "Scholar",
    description: "Scholarship information",
    icon: "🎓",
  },
  [SECTION_TYPES.RECRUITERS]: {
    name: "Recruiters",
    description: "Recruiter information",
    icon: "💼",
  },
  [SECTION_TYPES.ELIGIBILITY]: {
    name: "Eligibility",
    description: "Eligibility criteria",
    icon: "📋",
  },
  [SECTION_TYPES.SPECIALISATION_AVAILABLE]: {
    name: "Specialisation",
    description: "Available specialisations",
    icon: "🎯",
  },
  [SECTION_TYPES.CAREER_PROSPECTS]: {
    name: "Career Prospects",
    description: "Career opportunities",
    icon: "💼",
  },
  [SECTION_TYPES.CERTIFICATION]: {
    name: "Certification",
    description: "Global certifications and professional credentials",
    icon: "🎖️",
  },
  [SECTION_TYPES.SUCCESS_STORIES]: {
    name: "Success Stories",
    description: "Student success stories",
    icon: "⭐",
  },
  [SECTION_TYPES.WHAT_MAKES_COURSE_DIFFERENT]: {
    name: "What Makes Course Different",
    description: "Course differentiators",
    icon: "✨",
  },
  [SECTION_TYPES.VICE_CHANCELLOR]: {
    name: "Vice Chancellor",
    description: "Vice Chancellor messages",
    icon: "👔",
  },
  [SECTION_TYPES.FACULTY]: {
    name: "Faculty",
    description: "Faculty information",
    icon: "👨‍🏫",
  },
  [SECTION_TYPES.TESTIMONIALS]: {
    name: "Testimonials",
    description: "Student testimonials",
    icon: "💬",
  },
  [SECTION_TYPES.MASTERCLASS]: {
    name: "Masterclass",
    description: "Masterclass information",
    icon: "🎯",
  },
  [SECTION_TYPES.PLACEMENTS]: {
    name: "Placements",
    description: "Placement information",
    icon: "🎯",
  },
  [SECTION_TYPES.MOBILE_APP]: {
    name: "Mobile App",
    description: "Mobile app information",
    icon: "📱",
  },
  [SECTION_TYPES.FAQ]: {
    name: "FAQ",
    description: "Frequently Asked Questions",
    icon: "❓",
  },
  [SECTION_TYPES.PROGRAM_STRUCTURE]: {
    name: "Program Structure",
    description: "Program structure with semesters and subjects",
    icon: "📖",
  },
  [SECTION_TYPES.ELECTIVE]: {
    name: "Elective",
    description: "Electives with categories, semesters and subjects",
    icon: "📝",
  },
  [SECTION_TYPES.PROGRAM_FEE]: {
    name: "Program Fee",
    description: "Program fee structure",
    icon: "💰",
  },
  [SECTION_TYPES.RICH_TEXT_EDITOR]: {
    name: "Rich Text Editor",
    description: "Rich text editor with content and status",
    icon: "📝",
  },
  [SECTION_TYPES.BLOG_WITH_LEAD]: {
    name: "Blog with lead",
    description: "Blog article with embedded lead form (recommended for blog pages)",
    icon: "📰",
  },
  [SECTION_TYPES.LATEST_BLOGS]: {
    name: "Latest blogs",
    description: "Latest blog cards with carousel",
    icon: "📑",
  },
  [SECTION_TYPES.POPULAR_BLOGS]: {
    name: "Popular blogs",
    description:
      "Featured card + list; add posts in CMS or auto-fill from published pages",
    icon: "🔥",
  },
  [SECTION_TYPES.BLOG_LIST]: {
    name: "Blog list",
    description: "Blog grid with category filters and load more",
    icon: "📚",
  },
  [SECTION_TYPES.COUNSELOR_CALL_CTA]: {
    name: "Counselor call CTA",
    description: "Phone pill with call counsellor button",
    icon: "📞",
  },
  [SECTION_TYPES.BREADCRUMB_BANNER]: {
    name: "Breadcrumb",
    description: "Manual breadcrumb trail (article column on blog pages)",
    icon: "🧭",
  },
  [SECTION_TYPES.BLOG_INLINE_IMAGE]: {
    name: "Blog inline image",
    description: "Upload an image with optional caption (article column on blog pages)",
    icon: "🖼️",
  },
  [SECTION_TYPES.FOOTER]: {
    name: "Footer",
    description: "Page footer",
    icon: "⬇️",
  },
  [SECTION_TYPES.SUBSCRIBE]: {
    name: "Subscribe",
    description: "Newsletter subscription",
    icon: "✉️",
  },
  [SECTION_TYPES.PROGRAM_CARDS]: {
    name: "Program Cards",
    description: "Program course cards",
    icon: "🎓",
  },
  [SECTION_TYPES.THANK_YOU]: {
    name: "Thank You",
    description: "Thank you page with background and rich text",
    icon: "🙏",
  },
  [SECTION_TYPES.REFERRAL_FORM]: {
    name: "Referral Form",
    description: "Referral form with banner and form fields",
    icon: "📝",
  },
  [SECTION_TYPES.INFLUENCER_FORM]: {
    name: "Influencer Form",
    description: "Influencer form with banner and form fields",
    icon: "👤",
  },
  [SECTION_TYPES.GRIEVANCE_FORM]: {
    name: "Grievance Form",
    description: "Configurable grievance/contact form that emails submissions",
    icon: "📮",
  },
  [SECTION_TYPES.MOBILE_LEAD_FORM]: {
    name: "Mobile Lead Form",
    description: "Mobile lead form with status toggle",
    icon: "📱",
  },
  [SECTION_TYPES.COUNSELING]: {
    name: "Counseling",
    description: "Counseling section with CTA button",
    icon: "🎯",
  },
  [SECTION_TYPES.COURSE_SPECIALISATIONS]: {
    name: "Course Specialisations",
    description: "Course specialisations with desktop and mobile variants",
    icon: "🎯",
  },
  [SECTION_TYPES.PROGRAM_PROSPECTS]: {
    name: "Program Prospects",
    description: "Program prospects section with career paths",
    icon: "🚀",
  },
  [SECTION_TYPES.ACCORDION]: {
    name: "Accordion",
    description: "Accordion section with expandable items",
    icon: "📋",
  },
  [SECTION_TYPES.LIVE_LECTURE]: {
    name: "Live Lecture",
    description: "Live lecture schedule widget with calendar view",
    icon: "📅",
  },
  [SECTION_TYPES.PROGRAM_BENEFITS]: {
    name: "Program Benefits",
    description: "Program benefits with cards and CTA button",
    icon: "🎁",
  },
  [SECTION_TYPES.COURSE_OUTCOME]: {
    name: "Course Outcome",
    description: "Course outcome careers section with career paths",
    icon: "🎯",
  },
  [SECTION_TYPES.CERTIFICATION]: {
    name: "Certification",
    description: "Certification carousel with images and details",
    icon: "🎓",
  },
  [SECTION_TYPES.APPLICATION_PROCESS]: {
    name: "Application Process",
    description: "Application process with eligibility and admission information",
    icon: "📝",
  },
  [SECTION_TYPES.SKILLS]: {
    name: "Skills",
    description: "Skills section with certifications and career paths",
    icon: "🎯",
  },
  [SECTION_TYPES.COMPARISON]: {
    name: "Comparison",
    description: "Comparison table: Online MBA vs Traditional vs Standalone",
    icon: "⚖️",
  },
  [SECTION_TYPES.LEADERS]: {
    name: "Leaders",
    description: "Leaders section with description and metadata cards",
    icon: "👤",
  },
  [SECTION_TYPES.WHO_IS_THIS_PROGRAM_FOR]: {
    name: "Who is this program for",
    description: "Who is this program for section with gradient and metadata cards",
    icon: "👥",
  },
  [SECTION_TYPES.BANNER_VIDEO]: {
    name: "Banner Video",
    description: "Video section banner with lead drop or video on click",
    icon: "🎬",
  },
  [SECTION_TYPES.PARA_TEXT]: {
    name: "Para Text",
    description: "Paragraph block with background, text color, and description",
    icon: "📄",
  },
  [SECTION_TYPES.AUTHOR_PROFILE]: {
    name: "Author Profile",
    description:
      "Author card, About column and the author's blogs. Pick an author — details autofill from Author Management",
    icon: "✍️",
  },
  [SECTION_TYPES.AUTHOR_CARD]: {
    name: "Author Card",
    description:
      "ONLY RENDERS ON A BLOG PAGE THAT HAS AN AUTHOR LINKED. Compact bio card — photo, name, article count and bio, all read from the linked author",
    icon: "🪪",
  },
  [SECTION_TYPES.AUTHOR_BYLINE]: {
    name: "Author Byline",
    description:
      "ONLY RENDERS ON A BLOG PAGE THAT HAS AN AUTHOR LINKED. \"By <author>\" with updated date, read time and views, plus share links",
    icon: "🖊️",
  },
  [SECTION_TYPES.HEADING]: {
    name: "Heading",
    description:
      "Configurable heading: text, tag (h1-h6), font family, desktop/mobile size, weight, line height, colors",
    icon: "🔠",
  },
  [SECTION_TYPES.SPECIALISATION_CAROUSEL]: {
    name: "Specialisation Carousel",
    description:
      "Scrollable specialisation cards (image or icon layout); each card opens a lead form, a URL or a rich-text modal",
    icon: "🎠",
  },
  [SECTION_TYPES.WALK_THROUGH]: {
    name: "Walk Through",
    description: "Virtual campus hero: background image, headline, View Video CTA",
    icon: "🎥",
  },
  [SECTION_TYPES.LIFE_MILESTONE]: {
    name: "Life Milestone",
    description: "Animated milestone stepper: title, steps, and hero image",
    icon: "🪜",
  },
  [SECTION_TYPES.ENROLLMENTS]: {
    name: "Enrollments",
    description:
      "Enrollment CTAs: framed banner (v1) or Program Enrollments — copy + lead popup CTA (v2)",
    icon: "✅",
  },
  [SECTION_TYPES.CASE_STUDY]: {
    name: "Case Study",
    description: "Case study cards with slider and CTA",
    icon: "📂",
  },
  [SECTION_TYPES.BRAND]: {
    name: "Brand",
    description: "Brand logos pill grid for leading brands",
    icon: "🏢",
  },
  [SECTION_TYPES.INSTITUTES]: {
    name: "Institutes",
    description: "Heading + text chips for leading financial institutions",
    icon: "🏛️",
  },
  [SECTION_TYPES.SKILL_CARDS]: {
    name: "Skill Cards",
    description:
      "Heading + grid of skill containers (image, title, subtitle, divider, paragraph) with View More reveal and closing CTA",
    icon: "🃏",
  },

};

